// src/redux/thunks/iconStocksActionThunks.js
import { createAsyncThunk } from '@reduxjs/toolkit';
import IconStocksAPI from './iconStocksAPI';

/**
 * Enhanced thunks with better error handling and validation
 */

/**
 * Fetch stock SVG by symbol
 * @param {string} symbol - Stock symbol to fetch
 */
export const fetchStockSvgThunk = createAsyncThunk(
  'iconStocks/fetchBySymbol',
  async (symbol, { rejectWithValue, getState }) => {
    try {
      // Validate input
      if (!symbol || typeof symbol !== 'string') {
        throw new Error('Invalid symbol provided');
      }

      const normalizedSymbol = symbol.toUpperCase().trim();

      // The `condition` callback below already bails out for cached/in-flight
      // symbols before this ever runs -- this check is a defensive fallback
      // for any caller that dispatches directly. (Was previously reading the
      // wrong state key, `state.stockIcons`, instead of the slice's real
      // mount point `state.stockLogo` -- silently never matched, so it never
      // actually deduplicated anything. Found by the pass-28 route sweep.)
      const state = getState();
      const existingIcon = state.stockLogo?.stockIcons?.[normalizedSymbol];

      if (existingIcon?.svg) {
        // Return cached data instead of making another API call
        return { symbol: normalizedSymbol, data: existingIcon };
      }

      const data = await IconStocksAPI.getBySymbol(normalizedSymbol);

      if (!data || !data.svg) {
        throw new Error('Invalid response from API');
      }

      return { symbol: normalizedSymbol, data };
    } catch (error) {
      const errorMessage = error.message || 'Error fetching stock SVG';
      console.warn(`fetchStockSvgThunk error for symbol ${symbol}:`, error);
      return rejectWithValue(errorMessage);
    }
  },
  {
    // Runs synchronously before dispatch -- this is what actually prevents
    // the duplicate-request storm: when many <StockLogo> instances for the
    // same symbol mount in the same tick (a ticker repeated across "Top
    // Gainers"/"Most Active"/the main grid), each one's dispatch is checked
    // against the *current* state at dispatch time, including symbols
    // already marked pending by an earlier dispatch in this same batch.
    condition: (symbol, { getState }) => {
      if (!symbol || typeof symbol !== 'string') return false;
      const normalizedSymbol = symbol.toUpperCase().trim();
      const state = getState();
      const alreadyCached = !!state.stockLogo?.stockIcons?.[normalizedSymbol]?.svg;
      const alreadyPending = state.stockLogo?.pendingSymbols?.includes(normalizedSymbol);
      return !alreadyCached && !alreadyPending;
    },
  }
);

/**
 * Create a new stock SVG
 * @param {Object} svgData - Object containing symbol and svg content
 * @param {string} svgData.symbol - Stock symbol
 * @param {string} svgData.svg - SVG content
 */
export const createStockSvgThunk = createAsyncThunk(
  'iconStocks/create',
  async (svgData, { rejectWithValue, getState }) => {
    try {
      // Validate input
      if (!svgData || typeof svgData !== 'object') {
        throw new Error('Invalid SVG data provided');
      }

      const { symbol, svg } = svgData;

      if (!symbol || typeof symbol !== 'string') {
        throw new Error('Invalid or missing symbol');
      }

      if (!svg || typeof svg !== 'string') {
        throw new Error('Invalid or missing SVG content');
      }

      const normalizedSymbol = symbol.toUpperCase().trim();

      // Check if symbol already exists
      const state = getState();
      // Slice is internally named 'stockIcons' but mounted at state.stockLogo
      // in the root reducer (see indexRedux.js) -- state.stockIcons has never
      // existed. Same bug shape already fixed once in fetchStockSvgThunk
      // above (pass 28); this and the other two call sites below were never
      // caught because nothing in the live UI calls them (confirmed via
      // full-repo grep, zero consumers).
      const existingIcon = state.stockLogo?.stockIcons?.[normalizedSymbol];

      if (existingIcon?.svg) {
        throw new Error(`Stock SVG already exists for symbol: ${normalizedSymbol}`);
      }

      const normalizedSvgData = {
        symbol: normalizedSymbol,
        svg: svg.trim()
      };

      const data = await IconStocksAPI.create(normalizedSvgData);
      
      if (!data || !data.svg || !data.symbol) {
        throw new Error('Invalid response from API');
      }

      return data;
    } catch (error) {
      const errorMessage = error.message || 'Error creating stock SVG';
      console.warn('createStockSvgThunk error:', error);
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Delete a stock SVG by ID
 * @param {number|string} svgId - ID of the SVG to delete
 */
export const deleteStockSvgThunk = createAsyncThunk(
  'iconStocks/delete',
  async (svgId, { rejectWithValue, getState }) => {
    try {
      // Validate input
      if (!svgId) {
        throw new Error('Invalid SVG ID provided');
      }

      // Check if the SVG exists in state
      const state = getState();
      const stockIcons = state.stockLogo?.stockIcons || {};

      const symbolToDelete = Object.keys(stockIcons).find(
        symbol => stockIcons[symbol].id === svgId
      );

      if (!symbolToDelete) {
        throw new Error(`Stock SVG not found with ID: ${svgId}`);
      }

      await IconStocksAPI.delete(svgId);
      
      return svgId;
    } catch (error) {
      const errorMessage = error.message || 'Error deleting stock SVG';
      console.warn(`deleteStockSvgThunk error for ID ${svgId}:`, error);
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Delete stock SVG by symbol (convenience method)
 * @param {string} symbol - Stock symbol to delete
 */
export const deleteStockSvgBySymbolThunk = createAsyncThunk(
  'iconStocks/deleteBySymbol',
  async (symbol, { rejectWithValue, getState, dispatch }) => {
    try {
      if (!symbol || typeof symbol !== 'string') {
        throw new Error('Invalid symbol provided');
      }

      const normalizedSymbol = symbol.toUpperCase().trim();
      
      // Get the SVG ID from state
      const state = getState();
      const stockIcon = state.stockLogo?.stockIcons?.[normalizedSymbol];

      if (!stockIcon || !stockIcon.id) {
        throw new Error(`Stock SVG not found for symbol: ${normalizedSymbol}`);
      }

      // Use the existing delete thunk
      return await dispatch(deleteStockSvgThunk(stockIcon.id)).unwrap();
    } catch (error) {
      const errorMessage = error.message || 'Error deleting stock SVG by symbol';
      console.warn(`deleteStockSvgBySymbolThunk error for symbol ${symbol}:`, error);
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Batch fetch multiple stock SVGs
 * @param {string[]} symbols - Array of stock symbols to fetch
 */
export const fetchMultipleStockSvgsThunk = createAsyncThunk(
  'iconStocks/fetchMultiple',
  async (symbols, { rejectWithValue, dispatch }) => {
    try {
      if (!Array.isArray(symbols) || symbols.length === 0) {
        throw new Error('Invalid symbols array provided');
      }

      const results = await Promise.allSettled(
        symbols.map(symbol => dispatch(fetchStockSvgThunk(symbol)).unwrap())
      );

      const successful = [];
      const failed = [];

      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          successful.push(result.value);
        } else {
          failed.push({
            symbol: symbols[index],
            error: result.reason
          });
        }
      });

      if (failed.length > 0) {
        console.warn('Some stock SVGs failed to fetch:', failed);
      }

      return {
        successful,
        failed,
        total: symbols.length
      };
    } catch (error) {
      const errorMessage = error.message || 'Error fetching multiple stock SVGs';
      console.warn('fetchMultipleStockSvgsThunk error:', error);
      return rejectWithValue(errorMessage);
    }
  }
);